import { Button, Form, Input, Tag } from 'antd'
import React from 'react'
import { isEmail, ReactMultiEmail } from 'react-multi-email'
import styled from 'styled-components/macro'
import DeliveryDaysSelect from 'src/components/DeliveryDaysField'
import {
  optionalLabel,
  requiredLabel,
  DEFAULT_ACCOUNT_NUMBER_HELPER_TEXT,
  DEFAULT_ACCOUNT_NUMBER_PLACEHOLDER,
  LOGO_URL_HELPER_TEXT,
  LOGO_URL_PLACEHOLDER,
  SECOND_TITLE_HELPER_TEXT,
  SECOND_TITLE_PLACEHOLDER,
} from 'src/utils/supplierFormFieldLabels'
import {
  getEmptySupplierCreateForm,
  mapSupplierCreateFormValues,
  MIN_ORDER_POUNDS_HELPER_TEXT,
  MIN_ORDER_POUNDS_PLACEHOLDER,
  prepareSupplierCreatePayload,
  isValidMinOrderPoundsInput,
} from 'src/utils/supplierCreateForm'
import { getSupplierCreateBrandTagColor } from 'src/utils/supplierCreateBrandTag'
import {
  ORDER_CUTOFF_INSTRUCTIONS_HELPER_TEXT,
  ORDER_CUTOFF_INSTRUCTIONS_LABEL,
  ORDER_CUTOFF_TIME_HELPER_TEXT,
  ORDER_CUTOFF_TIME_LABEL,
} from 'src/utils/supplierOrderCutOff'

const emptyCreateForm = getEmptySupplierCreateForm()

const SecondEmailInput = ({ value = [], onChange }) => (
  <Div>
    <Span>{optionalLabel('Second email addresses')}</Span>
    <ReactMultiEmail
      placeholder=''
      emails={value}
      onChange={onChange}
      validateEmail={email => isEmail(email)}
      getLabel={(email, index, removeEmail) => (
        <InnerDiv data-tag key={index}>
          {email}
          <span data-tag-handle onClick={() => removeEmail(index)}>
            ×
          </span>
        </InnerDiv>
      )}
    />
  </Div>
)

const DeliveryDaysFormField = ({ deliveryDays, onChange }) => (
  <DeliveryDaysFieldWrap>
    <FieldLabel>{requiredLabel('Delivery days')}</FieldLabel>
    <DeliveryDaysSelect
      deliveryDays={deliveryDays}
      onChange={onChange}
      isDisabled={false}
    />
  </DeliveryDaysFieldWrap>
)

const SupplierCard = ({
  saveSupplier,
  form,
  inModal = false,
  isSubmitting = false,
  isPlatformAdmin = false,
  createBrandContext,
}) => {
  const { getFieldDecorator, getFieldValue } = form
  const isIronWeb =
    String(getFieldValue('integrationType') || '')
      .trim()
      .toLowerCase() === 'ironweb'

  const handleSubmit = event => {
    event?.preventDefault?.()

    if (!createBrandContext?.ok) return

    form.validateFields(async (error, values) => {
      if (error) return

      const success = await saveSupplier(
        prepareSupplierCreatePayload(mapSupplierCreateFormValues(values)),
      )

      if (success) {
        form.resetFields()
      }
    })
  }

  return (
    <Form onSubmit={handleSubmit}>
      <FormShell $inModal={inModal}>
        {!inModal && <h4>Create supplier</h4>}
        {createBrandContext?.ok ? (
          <BrandTagRow>
            <BrandTagLabel>Supplier type</BrandTagLabel>
            <Tag
              color={getSupplierCreateBrandTagColor(
                createBrandContext.displayTag,
              )}
            >
              {createBrandContext.displayTag}
            </Tag>
          </BrandTagRow>
        ) : (
          <BrandContextError>{createBrandContext?.error}</BrandContextError>
        )}
        <RequiredNote>Fields marked with * are required.</RequiredNote>

        <FormSection title='Identity'>
          <FormItem>
            {getFieldDecorator('title', {
              initialValue: emptyCreateForm.title,
              rules: [
                {
                  required: true,
                  message: 'Please input the title',
                },
              ],
            })(
              <Input
                addonBefore={requiredLabel('Title')}
                placeholder='Title'
              />,
            )}
          </FormItem>

          <FormItem extra={SECOND_TITLE_HELPER_TEXT}>
            {getFieldDecorator('secondTitle', {
              initialValue: emptyCreateForm.secondTitle,
              rules: [
                {
                  required: true,
                  message: 'Please enter the price list name',
                },
              ],
            })(
              <Input
                addonBefore={requiredLabel('Price list name')}
                placeholder={SECOND_TITLE_PLACEHOLDER}
              />,
            )}
          </FormItem>

          <FormItem extra={LOGO_URL_HELPER_TEXT}>
            {getFieldDecorator('logoUrl', {
              initialValue: emptyCreateForm.logoUrl,
              rules: [
                {
                  required: true,
                  message: 'Please enter the logo URL',
                },
                {
                  type: 'url',
                  message: 'Please enter a valid image URL',
                },
              ],
            })(
              <Input
                addonBefore={requiredLabel('Logo URL')}
                placeholder={LOGO_URL_PLACEHOLDER}
              />,
            )}
          </FormItem>
        </FormSection>

        <FormSection title='Contact'>
          <FormItem>
            {getFieldDecorator('emailAddress', {
              initialValue: emptyCreateForm.emailAddress,
              rules: [
                {
                  required: true,
                  message: 'Please input the email address',
                },
                {
                  type: 'email',
                  message: 'The input is not valid email',
                },
              ],
            })(
              <Input
                addonBefore={requiredLabel('Order email address')}
                placeholder='Order email address'
              />,
            )}
          </FormItem>

          <FormItem>
            {getFieldDecorator('phoneNumber', {
              initialValue: emptyCreateForm.phoneNumber,
              rules: [
                {
                  required: true,
                  message: 'Please input a contact number',
                },
              ],
            })(
              <Input
                addonBefore={requiredLabel('Contact number')}
                placeholder='Contact number'
              />,
            )}
          </FormItem>

          <FormItem>
            {getFieldDecorator('secondEmailAddresses', {
              initialValue: emptyCreateForm.secondEmailAddresses,
            })(<SecondEmailInput />)}
          </FormItem>
        </FormSection>

        <FormSection title='Order settings'>
          <FormItem extra={DEFAULT_ACCOUNT_NUMBER_HELPER_TEXT}>
            {getFieldDecorator('accountNumber', {
              initialValue: emptyCreateForm.accountNumber,
              rules: [
                {
                  required: true,
                  message: 'Please enter the default account number',
                },
              ],
            })(
              <Input
                addonBefore={requiredLabel('Default account number')}
                placeholder={DEFAULT_ACCOUNT_NUMBER_PLACEHOLDER}
              />,
            )}
          </FormItem>

          <FormItem extra={MIN_ORDER_POUNDS_HELPER_TEXT}>
            {getFieldDecorator('minOrderValue', {
              initialValue: emptyCreateForm.minOrderValue,
              rules: [
                {
                  validator: (_rule, value, callback) => {
                    if (!isValidMinOrderPoundsInput(value)) {
                      callback('Please enter a valid minimum order in pounds')
                      return
                    }

                    callback()
                  },
                },
              ],
            })(
              <Input
                addonBefore={requiredLabel('Minimum order (£)')}
                placeholder={MIN_ORDER_POUNDS_PLACEHOLDER}
              />,
            )}
          </FormItem>

          <FormItem>
            {getFieldDecorator('deliveryDays', {
              initialValue: emptyCreateForm.deliveryDays,
              valuePropName: 'deliveryDays',
              rules: [
                {
                  validator: (_rule, value, callback) => {
                    if (!value || value.length === 0) {
                      callback('Please select at least one delivery day')
                      return
                    }

                    callback()
                  },
                },
              ],
            })(<DeliveryDaysFormField />)}
          </FormItem>

          <FormItem extra={ORDER_CUTOFF_TIME_HELPER_TEXT}>
            {getFieldDecorator('orderCutOffTime', {
              initialValue: emptyCreateForm.orderCutOffTime,
              rules: [
                {
                  validator: (_rule, value, callback) => {
                    if (!value) {
                      callback()
                      return
                    }

                    if (!/^(?:[01]?\d|2[0-3]):[0-5]\d$/.test(value)) {
                      callback('Use 24-hour time HH:MM (00:00–23:59)')
                      return
                    }

                    callback()
                  },
                },
              ],
            })(
              <Input
                type='time'
                addonBefore={optionalLabel(ORDER_CUTOFF_TIME_LABEL)}
                placeholder='16:00'
              />,
            )}
          </FormItem>

          <FormItem extra={ORDER_CUTOFF_INSTRUCTIONS_HELPER_TEXT}>
            {getFieldDecorator('orderCutOffInstructions', {
              initialValue: emptyCreateForm.orderCutOffInstructions,
            })(
              <Input
                addonBefore={optionalLabel(ORDER_CUTOFF_INSTRUCTIONS_LABEL)}
                placeholder='Order before 4pm for next day delivery'
              />,
            )}
          </FormItem>
        </FormSection>

        <FormSection title='Advanced'>
          <FormItem>
            {getFieldDecorator('integrationType', {
              initialValue: emptyCreateForm.integrationType || '',
            })(
              <Input
                addonBefore={optionalLabel('Integration type')}
                placeholder='Integration type'
              />,
            )}
          </FormItem>

          {isPlatformAdmin && isIronWeb && (
            <FormItem extra='After you save a new agreement number, the next file for that number can import.'>
              {getFieldDecorator('ironWebAgreementNumber', {
                initialValue: '',
              })(
                <Input
                  addonBefore={optionalLabel('IronWeb agreement number')}
                  placeholder='Agreement number'
                />,
              )}
            </FormItem>
          )}

          <FormItem>
            {getFieldDecorator('vendorCode', {
              initialValue: emptyCreateForm.vendorCode,
            })(
              <Input
                addonBefore={optionalLabel('Vendor code')}
                placeholder='Vendor code'
              />,
            )}
          </FormItem>

          <FormItem>
            {getFieldDecorator('dc', {
              initialValue: emptyCreateForm.dc || '',
            })(
              <Input
                addonBefore={optionalLabel('DC')}
                placeholder='Not Applicable'
              />,
            )}
          </FormItem>
        </FormSection>

        <FormItem>
          <Button
            type='primary'
            htmlType='submit'
            loading={isSubmitting}
            disabled={!createBrandContext?.ok}
          >
            Add supplier
          </Button>
        </FormItem>
      </FormShell>
    </Form>
  )
}

const FormSection = ({ title, children }) => (
  <Section>
    <SectionTitle>{title}</SectionTitle>
    {children}
  </Section>
)

const FormShell = styled.div`
  ${({ $inModal, theme }) =>
    $inModal
      ? ''
      : `
    border: 1px solid ${theme.colours.panSilver};
    border-radius: 4px;
    background: ${theme.colours.mayoWhite};
    padding: 24px;
    margin-top: 16px;
  `}
`

const RequiredNote = styled.p`
  margin: 0 0 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const BrandTagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`

const BrandTagLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.otherBlack};
`

const BrandContextError = styled.p`
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 4px;
  background: #fff1f0;
  border: 1px solid #ffa39e;
  color: #cf1322;
  font-size: 13px;
`

const Section = styled.div`
  margin-bottom: 20px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colours.panSilver};

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 12px;
  }
`

const SectionTitle = styled.h5`
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.otherBlack};
`

const FieldLabel = styled.div`
  margin-bottom: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.otherBlack};
`

const DeliveryDaysFieldWrap = styled.div`
  width: 100%;
`

const FormItem = styled(Form.Item)`
  margin-bottom: 8px !important;

  .ant-form-extra {
    color: ${({ theme }) => theme.colours.oliveGrey};
    font-size: 12px;
    margin-top: 4px;
  }
`

const InnerDiv = styled.div`
  background-color: ${({ theme }) => theme.colours.lettuceGreen} !important;
`

const Span = styled.span`
  padding: 0 10px;
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  background-color: ${({ theme }) => theme.colours.mayoWhite};
`

const Div = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  .react-multi-email {
    border: 1px solid ${({ theme }) => theme.colours.panSilver};
    border-radius: 0;
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }
  .react-multi-email:hover,
  .react-multi-email.focused {
    border: 1px solid ${({ theme }) => theme.colours.lettuceGreen} !important;
  }
`

const WrappedSupplierCard = Form.create({ name: 'add_supplier' })(SupplierCard)

export default WrappedSupplierCard
